import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { GoogleLogin } from '@react-oauth/google';
import { recoverAccount } from 'core/crud/auth.crud';
import { getScrollBehavior } from 'core/utils/motion';
import { AuthProviderButton, Button, Dialog, FormField, InputCheckbox, InputPassword, InputText, Loading, TextHtml } from 'ui';
import LineWithText from 'core/modules/atoms/LineWithText';
import Logo from 'core/modules/atoms/Logo/Logo';
import ForgotPasswordForm from 'core/modules/components/forms/ForgotPasswordForm/ForgotPasswordForm';
import LoginOraculo from 'core/modules/components/ssoOraculo/LoginOraculo';
import { loginOraculo } from 'core/modules/components/ssoOraculo/serviceOraculo';
import * as coreEntities from 'core/store/index';
import { logger } from 'shared/infrastructure/logger';
import { successResponse } from 'core/utils/utils';
import { PRIVACY, TERMS } from 'edelvives/config/config';
import { defaultConfig } from 'edelvives/config/environment';
import initLayoutConfig from 'edelvives/config/layout/LayoutConfig';
import { getUrlSupport } from 'edelvives/utils/utils';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

const POLITICS = { LEGAL: 'legal', PRIVACY: 'privacy', IA: 'ia' };
const USER_ABANDONED_MSAL_ERRORS = new Set(['user_cancelled']);

function isRedirectTimeout(error) {
  return error?.errorCode === 'timed_out' && error?.subError === 'failed_to_redirect';
}

const FORGOT_PASSWORD_STATE = { INITIAL: 'initial', LOADING: 'loading', SUCCESS: 'success' };

const LoginForm = (props) => {
  const { addToast } = useToasts();
  const { instance, accounts, inProgress } = useMsal();

  const { register, handleSubmit, errors } = useForm();
  const { t, onSubmit, onSSO, loading, showRememberMe, showForgotPassword, onRegister } = props;
  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  // `null` (cerrado) o una clave de `POLITICS`: arrancar en `false` mezclaba boolean con string y
  // hacía la comparación con `POLITICS.PRIVACY` imposible de tipar (S3403).
  const [showPolitics, setShowPolitics] = useState(null);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [forgotPasswordState, setForgotPasswordState] = useState(FORGOT_PASSWORD_STATE.INITIAL);
  let lang = language && Object.keys(PRIVACY).includes(language) ? language : 'es';

  const account = useAccount(accounts[0] || {});

  useEffect(() => {
    if (inProgress === InteractionStatus.None && account) {
      // Get access token silently for logged in user
      instance
        .acquireTokenSilent({
          scopes: ['User.Read'],
          account: account,
        })
        .then((response) => {
          if (response && response.accessToken) {
            onSSO(response.accessToken, 'microsoft');
          }
        })
        .catch((error) => {
          console.error('Token acquisition failed:', error);
        });
    }
  }, [inProgress, account]);

  useEffect(() => {
    if (showPolitics !== POLITICS.PRIVACY) return undefined;

    // Con cleanup: sin él, el timer sobrevive al desmontaje y dispara sobre un `document` que ya
    // no existe (revienta el worker de Jest en CI; en la app, scrollearía hacia un diálogo cerrado).
    const scrollTimeoutId = setTimeout(() => {
      const privacyId = document.getElementById('privacy-policy');
      if (privacyId) {
        privacyId.scrollIntoView({
          behavior: getScrollBehavior(),
        });
      }
    }, 100);

    return () => clearTimeout(scrollTimeoutId);
  }, [showPolitics]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const interval = setInterval(() => {
        loginOraculo(interval, onSSO);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [inProgress]);

  const onLoginMicrosoft = () => {
    // MSAL rechaza cualquier login mientras procesa el arranque, el retorno de Microsoft o un clic
    // anterior (interaction_in_progress, TANGERINE-FRONTOFFICE-3TC): ese clic sobra, no es un error.
    if (inProgress !== InteractionStatus.None) return;

    // Al volver atrás desde Microsoft, el navegador restaura esta página del bfcache y el temporizador de 30 s que
    // MSAL dejó al redirigir rechaza con timed_out/failed_to_redirect (TANGERINE-FRONTOFFICE-3KN). Sin esa
    // restauración, el mismo error es una navegación bloqueada o tan lenta que MSAL ya ha borrado el login en curso.
    let wasRestoredFromBfcache = false;
    const onPageShow = (event) => {
      if (event.persisted) wasRestoredFromBfcache = true;
    };
    window.addEventListener('pageshow', onPageShow);

    instance
      .loginRedirect({
        scopes: ['User.Read'],
        redirectStartPage: window.location.href,
      })
      .catch((error) => {
        // MSAL ya tenía otra interacción en curso: reintentar no sirve y no es un error de la app.
        if (error?.errorCode === 'interaction_in_progress') return;
        // Que el usuario cancele el login o vuelva atrás desde Microsoft no es un fallo de la app (DEU-322).
        const isAbandonedByUser = USER_ABANDONED_MSAL_ERRORS.has(error?.errorCode) || (wasRestoredFromBfcache && isRedirectTimeout(error));
        const log = isAbandonedByUser ? logger.info : logger.error;
        log('Microsoft login redirect failed', { tags: { feature: 'microsoft-login', msal_error: error?.errorCode }, error });
      })
      .finally(() => window.removeEventListener('pageshow', onPageShow));
  };

  const onLogin = (data) => {
    if (onSubmit) onSubmit(data.email, data.password);
  };

  const onLoginSSO = (token) => {
    if (onSSO) onSSO(token, 'google');
  };

  const closeShowForgotPasswordDialog = () => {
    setShowForgotPasswordDialog(false);
    setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
  };

  async function onSendChangePassword(email) {
    setForgotPasswordState(FORGOT_PASSWORD_STATE.LOADING);
    const response = await recoverAccount(email, { link: defaultConfig.BASE_URL_ + 'recover-password?token=[TOKEN]&lang=[LANG]' });
    if (successResponse(response)) {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.SUCCESS);
    } else {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
      addToast(t(`login:User not exist`), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  function onOpenForgotPassword() {
    setShowForgotPasswordDialog(true);
  }

  function onShowLegal() {
    onShowPolitics(POLITICS.LEGAL);
  }

  function onShowPrivacy() {
    onShowPolitics(POLITICS.PRIVACY);
  }

  function onShowAiPolitics() {
    onShowPolitics(POLITICS.IA);
  }

  function onCloseShowPolitics() {
    setShowPolitics(null);
  }

  function onShowPolitics(selection) {
    const politics = selection === POLITICS.PRIVACY ? PRIVACY[lang] : TERMS[lang];
    if (politics?.endsWith('pdf')) {
      if (selection === POLITICS.PRIVACY) window.open(defaultConfig.BASE_URL_ + 'privacy', '_blank');
      if (selection === POLITICS.LEGAL) window.open(defaultConfig.BASE_URL_ + 'terms', '_blank');
      if (selection === POLITICS.IA) window.open(defaultConfig.BASE_URL_ + 'ia', '_blank');
    } else {
      setShowPolitics(selection);
    }
  }

  return (
    <div className="login-form">
      {forgotPasswordState === FORGOT_PASSWORD_STATE.LOADING && <Loading />}
      <div className="login-form__logo">
        <div className="login-form__item">
          <Logo size="big" />
        </div>
      </div>
      {/* `noValidate` is required: `isRequired` makes FormField inject the native `required`
          attribute, and native constraint validation would abort the submit event, so
          react-hook-form would never run and our own error messages would never render. */}
      <form noValidate onSubmit={handleSubmit(onLogin)} className="login-form__form">
        <div className="login-form__item">
          <h3 className="login-form__title">{t('Log in')}</h3>
        </div>
        <div className="login-form__item">
          <FormField error={!!errors.email} errorMessage={t('auth:Enter your username')} isRequired label={t('auth:User')}>
            <InputText icon="user" iconLeft={true} placeholder={t('auth:Username')} name="email" referer={register({ required: true })} />
          </FormField>
        </div>
        <div className="login-form__item">
          <FormField error={!!errors.password} errorMessage={t('auth:Enter your password')} isRequired label={t('auth:Password')}>
            <InputPassword iconLeft={true} placeholder={t('auth:Password')} name="password" referer={register({ required: true })} />
          </FormField>
        </div>
        <div className="login-form__item login-form__item--subactions">
          {showRememberMe && (
            <div className="login-form__checkbox-container">
              <InputCheckbox label={t('Remember me')} />
            </div>
          )}
          {showForgotPassword && (
            <button
              aria-haspopup="dialog"
              className="button-accessibility-only login-form__text login-form__text--forget"
              onClick={onOpenForgotPassword}
              type="button"
            >
              {t('Forgot your password?')}
            </button>
          )}
        </div>
        <div className="login-form__item">
          <Button testid="btn-login" text={t('Log in')} isLoading={loading} type="submit" isFullWidth />
        </div>
        {
          <>
            <div className="login-form__item">
              <LineWithText text={t('Or')} />
            </div>

            <div className="login-form__third-parties">
              <div className="login-form__item">
                <AuthProviderButton testid="btn-sso-microsoft" provider="microsoft" text={t('login:Login with Microsoft')} onClick={onLoginMicrosoft} />
              </div>

              {initLayoutConfig.login.showOraculo && (
                <div className="login-form__item">
                  <LoginOraculo text={t('login:Login with Polp')} />
                </div>
              )}

              <div className="login-form__item">
                <GoogleLogin
                  width="376px"
                  text="signin_with"
                  onSuccess={(credentialResponse) => {
                    // console.log(getTokenUserDetails(credentialResponse.credential));
                    onLoginSSO(credentialResponse.credential);
                  }}
                  onError={(response) => {
                    console.log('google fail ', response);
                  }}
                  locale={i18next.language}
                />
              </div>
            </div>
          </>
        }
        <div className="login-form__politics">
          <button aria-haspopup="dialog" className="button-accessibility-only login-form__legal-advice" onClick={onShowLegal} type="button">
            {t('login:Legal advice')}
          </button>
          <button aria-haspopup="dialog" className="button-accessibility-only login-form__privacy" onClick={onShowPrivacy} type="button">
            {t('login:Privacy')}
          </button>
          <button aria-haspopup="dialog" className="button-accessibility-only login-form__ai-politics" onClick={onShowAiPolitics} type="button">
            {t('login:AI Politics')}
          </button>
          <a className="button-accessibility-only login-form__support" href={getUrlSupport(navigator.language)} rel="noopener noreferrer" target="_blank">
            {t('common:Technical support')}
          </a>
        </div>
      </form>
      <div className="login-form__signup">
        <button className="button-accessibility-only login-form__signup-action" onClick={onRegister} type="button">
          <span className="login-form__text">
            {t('If you don’t have an account yet,')}
            <span className="login-form__link"> {t('register here')}</span>
          </span>
        </button>
      </div>
      <Dialog
        size="xl"
        className="dialog-terms"
        title={t(showPolitics === POLITICS.PRIVACY ? 'login:Privacy' : 'login:Legal advice').toUpperCase()}
        isOpen={Boolean(showPolitics)}
        onClose={onCloseShowPolitics}
      >
        <TextHtml text={showPolitics === POLITICS.PRIVACY ? PRIVACY[lang] : TERMS[lang]} />
      </Dialog>
      <Dialog
        size="s"
        className="dialog-forgot-password"
        title={t('recover:recover password')}
        isOpen={showForgotPasswordDialog}
        onClose={closeShowForgotPasswordDialog}
      >
        <ForgotPasswordForm t={t} state={forgotPasswordState} onCancel={closeShowForgotPasswordDialog} onSuccess={onSendChangePassword} />
      </Dialog>
    </div>
  );
};

LoginForm.propTypes = {
  loading: PropTypes.bool,
  onRegister: PropTypes.func,
  onSSO: PropTypes.func,
  onSubmit: PropTypes.func,
  showForgotPassword: PropTypes.bool,
  showRememberMe: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation(['login', 'auth', 'common', 'recover'])(LoginForm);
